// ========================================================================
// 蛋叔渠道 V2 C 端 Action（DetailsDsV2.vue / SpecPopupDsV2.vue 共用）
//
// 设计要点：
// 1) 详情接口：GET /dsMallV2/queryProductDetail —— 后端按 cardId 换算 showPrice / 单位，并附带
//    与列表同源的 labelList、配送范围 deliveryScopeDesc、蛋叔 cityId（拉门店用）。
// 2) 配送规则：POST /dsMallV2/queryDistributionRulesV2 —— 传 addressId + specIds + quantitys，
//    服务端按 addressId 查地址填充三方 city_name/area/addr/area_code；前端仅做地址展示拼接。
//    透传三方拆单 Map；本组件按单规格使用，取 Map 里第一个 key 对应规则。
// 3) 门店列表：GET /dsMallV2/queryShopList —— 传详情 cityId；有定位时附带 lng/lat，后端算距离排序。
// 4) 下单：本模块不直接下单。加购走老 /shoppingCart/add；立即购走老 /product/order/prePurchase
//    → /confirmOrder。三方下单（/dsMallV2/submitOrderV2）由后端在支付完成回调中调用。
// ========================================================================

import router from "../../router/index";
import httpApi from "../../http/api";
import { Toast } from "vant";
import { ensureUserLngLat } from "../../utils/TMapUtils.js";

/**
 * 加载 V2 详情。
 * @param {string} cityCode      城市编码
 * @param {string} thirdProductId 商品 id（带 DS 前缀）
 * @param {string} cardId        福利卡 id（必传）
 */
export async function loadV2Detail(cityCode, thirdProductId, cardId) {
  if (!cityCode || !thirdProductId) {
    Toast("商品信息缺失");
    return null;
  }
  if (!cardId) {
    Toast("请先选择福利卡");
    return null;
  }
  const res = await httpApi.dsV2QueryProductDetail(cityCode, thirdProductId, cardId);
  if (res.code !== 0) {
    Toast(res.msg || "加载详情失败");
    return null;
  }
  return res.data || null;
}

/* =====================================================================
 * V2 配送规则解析（严格对齐三方文档 /channelapi/order/get_distribution_rules）：
 */
export async function queryDistributionRulesV2(form, options = {}) {
  const { silent = false } = options;
  const res = await httpApi.dsV2QueryDistributionRules(form);
  if (res.code !== 0) {
    if (!silent) Toast(res.msg || "配送规则查询失败");
    return [];
  }
  const raw = res.data || {};
  const groups = [];
  Object.keys(raw).forEach(key => {
    const rules = raw[key];
    groups.push({
      key,
      rules,
      orderGroup: parseRules(key, rules),
    });
  });
  return groups;
}

/**
 * 是否可加购 
 * 业务约定: 自提 / 商家配送 因为带日期 / 时段 / 门店等强约束,加购后若再切换涉及大量再校验,
 * 仅允许"纯快递商品"加购。只要支持自提或商家配送之一,即禁止加购。
 */
export function computeShowAddcardFromRules(rules) {
  if (!rules || rules.is_distribution !== "1") return false;

  const hasShop = rules.can_take === "1"
      && Array.isArray(rules.validate_take_dates)
      && rules.validate_take_dates.length > 0;
  const hasShip = rules.can_ship === "1"
      && Array.isArray(rules.validate_delivery_dates)
      && rules.validate_delivery_dates.length > 0;
  if (hasShop || hasShip) return false; // 含自提 / 商家配送任一 → 禁止加购

  return rules.can_same === "1" && !!rules.validate_same_row;
}

/**
 * 把 V2 单组规则解析成 SpecPopupDsV2 内部使用的 orderGroup。
 *
 * 输出字段：
 *   sendSelectType: 0 未选 / 1 自提 / 2 快递 / 3 商家配送
 *   shipType:       'shop'/'same'/'delivery'   （与后端 ShipTypeEnum 对齐）
 *   shipDate / shipTimeText / deliveryAmount    （根据当前选中确定）
 *   sameDeliveryAmount                         （快递运费，便于切回快递时一键回填）
 *   takeDates: [{ date, dateText, times: [] }]
 *   deliveryDates: [{ date, dateText, deliveryAmount, times: [] }]
 *
 * 默认选中策略（与老 SpecPopup 一致）：优先级 快递 > 商家配送 > 自提；选了第一项后不再覆盖。
 */
function parseRules(specIdsJoin, rules) {
  const r = rules || {};
  const isDistribution = r.is_distribution === "1";
  const canSame = r.can_same === "1";
  const canShip = r.can_ship === "1";
  const canTake = r.can_take === "1";

  const result = {
    specIdsJoin,
    isDistribution,
    canSame,
    canShip,
    canTake,
    deliveryText: r.delivery_text || "",

    sameDeliveryAmount: "0",
    deliveryDates: [],
    takeDates: [],

    sendSelectType: 0,
    shipType: "",
    shipDate: "",
    shipTimeText: "",
    deliveryAmount: "0",
  };

  if (!isDistribution) return result;

  // 快递（V2: object，仅 delivery_amount）
  if (canSame && r.validate_same_row && typeof r.validate_same_row === "object") {
    result.sameDeliveryAmount = r.validate_same_row.delivery_amount || "0";
  }

  // 商家配送（V2: array<{date, date_text, delivery_amount, validate_delivery_times: array}>）
  if (canShip && Array.isArray(r.validate_delivery_dates)) {
    result.deliveryDates = r.validate_delivery_dates.map(it => ({
      date: it.date,
      dateText: it.date_text,
      deliveryAmount: it.delivery_amount || "0",
      times: Array.isArray(it.validate_delivery_times) ? it.validate_delivery_times : [],
    }));
  }

  // 自提（V2: array<{date, date_text, validate_take_times: array}>）
  if (canTake && Array.isArray(r.validate_take_dates)) {
    result.takeDates = r.validate_take_dates.map(it => ({
      date: it.date,
      dateText: it.date_text,
      times: Array.isArray(it.validate_take_times) ? it.validate_take_times : [],
    }));
  }

  // 默认选中（快递 > 商家配送 > 自提）
  if (canSame) {
    result.sendSelectType = 2;
    result.shipType = "same";
    result.deliveryAmount = result.sameDeliveryAmount;
  } else if (canShip && result.deliveryDates.length > 0) {
    const first = result.deliveryDates[0];
    result.sendSelectType = 3;
    result.shipType = "delivery";
    result.shipDate = first.date;
    result.shipTimeText = first.times[0] || "";
    result.deliveryAmount = first.deliveryAmount;
  } else if (canTake && result.takeDates.length > 0) {
    const first = result.takeDates[0];
    result.sendSelectType = 1;
    result.shipType = "shop";
    result.shipDate = first.date;
    result.shipTimeText = first.times[0] || "";
    result.deliveryAmount = "0";
  }

  return result;
}

/**
 * 配送方式（一级）展示：基于 orderGroup 输出 SendType picker 第一列可选项。
 */
export function buildSendTypeOptions(orderGroup) {
  const opts = [];
  if (!orderGroup) return opts;
  if (orderGroup.canSame) opts.push({ value: "same", text: "快递配送" });
  if (orderGroup.canShip && orderGroup.deliveryDates.length > 0) opts.push({ value: "delivery", text: "商家配送" });
  if (orderGroup.canTake && orderGroup.takeDates.length > 0) opts.push({ value: "shop", text: "门店自提" });
  return opts;
}

/**
 * 把 V2 orderGroup 解析结果转为 SendType 滚动 picker 三级 columns，严格对齐 V2 接口字段：
 *
 *   ┌─ 快递配送   ─ "运费"            ─ "免运费" / "X{unit}"
 *   ├─ 商家配送   ─ "X月X日 周X"      ─ "11:00-12:00"
 *   └─ 门店自提   ─ "X月X日 周X"      ─ "11:00-12:00"
 *
 * - 快递只有运费（无日期 / 时段），中间插入"运费"占位让三级齐整；
 * - 商家配送的运费按日期挂在 picker 的 value 上，便于选完后回查；
 * - 自提没有运费。
 *
 * @param {Object} orderGroup parseRules 输出
 * @param {string} unit 福利卡卡点单位（点 / 票 / 次 / 金豆）
 */
export function buildSendTypeColumns(orderGroup, unit) {
  const safeUnit = unit || "点";
  const columns = [];
  if (!orderGroup) return columns;

  // 快递
  if (orderGroup.canSame) {
    const amt = orderGroup.sameDeliveryAmount;
    const amountText = !amt || amt === "0" ? "免运费" : `${amt}${safeUnit}`;
    columns.push({
      text: "快递配送",
      value: "same",
      children: [
        {
          text: "运费",
          children: [{ text: amountText, value: amt || "0" }],
        },
      ],
    });
  }

  // 商家配送
  if (orderGroup.canShip && orderGroup.deliveryDates.length > 0) {
    columns.push({
      text: "商家配送",
      value: "delivery",
      children: orderGroup.deliveryDates.map(d => ({
        text: d.dateText || d.date,
        // 把 当天运费 + 原始日期 一起放 value，便于选完后回查
        value: { date: d.date, deliveryAmount: d.deliveryAmount },
        children: (d.times || []).map(tm => ({ text: tm, value: tm })),
      })),
    });
  }

  // 门店自提
  if (orderGroup.canTake && orderGroup.takeDates.length > 0) {
    columns.push({
      text: "门店自提",
      value: "shop",
      children: orderGroup.takeDates.map(d => ({
        text: d.dateText || d.date,
        value: { date: d.date },
        children: (d.times || []).map(tm => ({ text: tm, value: tm })),
      })),
    });
  }

  return columns;
}

/**
 * SendType picker 选完后，把 vant 输出的 record（数组形式 [一级文本, 二级文本, 三级文本]）
 * 翻译成可直接合入 selectedSpec / payload 的 deliveryInfo。
 *
 * @param {Array<string>} record vant SendType picker confirm 回调
 * @param {Object} orderGroup parseRules 输出
 * @param {string} unit 福利卡卡点单位
 */
export function resolveDeliveryInfoFromPick(record, orderGroup, unit) {
  const safeUnit = unit || "点";
  const result = {
    sendTypeStr: "",
    shipDate: "",
    shipTimeText: "",
    deliveryAmount: "0",
    sendSelectType: 0,
    shipType: "",
  };
  if (!Array.isArray(record) || record.length === 0 || !orderGroup) return result;
  const top = record[0];

  if (top === "快递配送") {
    const amt = orderGroup.sameDeliveryAmount || "0";
    result.sendSelectType = 2;
    result.shipType = "same";
    result.deliveryAmount = amt;
    result.sendTypeStr = !amt || amt === "0" ? "快递配送 免运费" : `快递配送 运费${amt}${safeUnit}`;
    return result;
  }

  if (top === "商家配送") {
    const dateText = record[1] || "";
    const timeText = record[2] || "";
    const hit = orderGroup.deliveryDates.find(d => (d.dateText || d.date) === dateText);
    const realDate = hit ? hit.date : dateText;
    const amt = hit ? hit.deliveryAmount : "0";
    result.sendSelectType = 3;
    result.shipType = "delivery";
    result.shipDate = realDate;
    result.shipTimeText = timeText;
    result.deliveryAmount = amt;
    result.sendTypeStr = !amt || amt === "0"
      ? `商家配送 ${dateText} ${timeText} 免运费`
      : `商家配送 ${dateText} ${timeText} 运费${amt}${safeUnit}`;
    return result;
  }

  if (top === "门店自提") {
    const dateText = record[1] || "";
    const timeText = record[2] || "";
    const hit = orderGroup.takeDates.find(d => (d.dateText || d.date) === dateText);
    const realDate = hit ? hit.date : dateText;
    result.sendSelectType = 1;
    result.shipType = "shop";
    result.shipDate = realDate;
    result.shipTimeText = timeText;
    result.deliveryAmount = "0";
    result.sendTypeStr = `门店自提 ${dateText} ${timeText}`;
    return result;
  }

  return result;
}

/**
 * V2 门店列表。有用户定位时传 lng/lat，由后端算距离并按近到远排序。
 */
export async function loadV2Shops(thirdBrandId, cityId) {
  if (!thirdBrandId || !cityId) {
    Toast("品牌或蛋叔城市 id 缺失，无法获取门店");
    return [];
  }
  const pos = await ensureUserLngLat();
  const res = await httpApi.dsV2QueryShopList(
    thirdBrandId,
    cityId,
    pos && pos.lng,
    pos && pos.lat
  );
  if (res.code !== 0) {
    Toast(res.msg || "门店列表查询失败");
    return [];
  }
  return res.data || [];
}

export function stripDsPrefix(id) {
  if (!id) return "";
  const str = String(id);
  return str.startsWith("DS") ? str.substring(2) : str;
}

/**
 * 加购 / 立即购统一提交。
 * 与老 Details.vue → submitBuyGoodsInfo 同形态：DS V2 商品继续走主体系（addShop / prePurchase）。
 *
 * @param {string} type      'addMyCart' 或 'buyNow'
 * @param {Object} payload   formatParamsDsV2 输出
 * @returns {Promise<boolean|undefined>}
 */
export async function submitBuyGoodsInfoDsV2(type, payload) {
  switch (type) {
    case "addMyCart": {
      const res = await httpApi.addShop(payload);
      if (res.code === 1) {
        Toast(res.msg);
        return false;
      }
      Toast.success("加入购物车成功");
      return true;
    }
    case "buyNow": {
      const showAddressBar = payload.showAddressBar; // 1 显示 / 0 隐藏（结算页地址栏）
      const res = await httpApi.prePurchase(payload);
      if (res.code === 0) {
        router.push({
          path: "/confirmOrder",
          query: {
            type: showAddressBar,
            code: res.data,
          },
        });
        return true;
      }
      Toast.fail(res.msg);
      return false;
    }
    default:
      return false;
  }
}

/**
 * 把 SpecPopupDsV2 提交事件拼装为后端 addShop / prePurchase 都能消费的 payload。
 * 字段尽量与老 Details.vue#formatParams 对齐，保证主体系（购物车 / 结算）继续工作。
 *
 * @param {Object} baseData     { userId, cardId, cityCode, productId, type }
 * @param {Object} specsData    { id, count }（id 为带 DS 前缀的 thirdSpecId，count 为购买数量）
 * @param {Object} deliveryData { addressInfo, shopInfo, deliveryInfo, sendSelectType, leaveWordText, notesText, showAddressBar }
 * @param {Object} extra        { v2: { thirdProductId, thirdSpecId } }（蛋叔 V2；地址仅 addressId）
 */
export function formatParamsDsV2(baseData, specsData, deliveryData, extra = {}) {
  const { userId, cardId, cityCode, productId, type } = baseData || {};
  const { id: skuId, count } = specsData || {};
  const { addressInfo, shopInfo, deliveryInfo, leaveWordText, notesText, showAddressBar, chargeIds, chargeQuantities, tastesName } = deliveryData || {};
  const { deliveryAmount, shipDate, shipTimeText, shipType } = deliveryInfo || {};
  const { addressId } = addressInfo || {};
  const { shopDetail, shopId, shopName } = shopInfo || {};
  const v2 = extra.v2 || {};

  return {
    userId,
    cardId,
    cityCode,
    productId,
    skuId,
    productNum: count,
    blessings: leaveWordText || "",
    buyerMsg: notesText || "",
    addressId,
    postMoney: deliveryAmount,
    shipType: shipType || "",
    shipDate: shipDate || "",
    shipTimeText: shipTimeText || "",
    shopDetail: shopDetail || "",
    shopId: shopId || "",
    shopName: shopName || "",
    type: type || "3",
    rechargeAccount: "",
    showAddressBar,
    // V2 透传字段（后端阶段 3+ 会落到购物车 redis、prePurchase 缓存，便于主下单时构造 V2 入参）
    thirdProductId: v2.thirdProductId || productId,
    thirdSpecId: v2.thirdSpecId || skuId,
    // 附件快照：
    chargeIds: Array.isArray(chargeIds) ? chargeIds : [],
    chargeQuantities: Array.isArray(chargeQuantities) ? chargeQuantities : [],
    // 口味
    tastesName: tastesName || "",
  };
}

export function goMyCart(type = 1) {
  router.push({ path: "/myCart", query: { type } });
}

export function goBack() {
  router.back();
}
